import { Box, Breadcrumbs, Button, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'

import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageRightColumn from '../../../components/page/pageRightColumn'

import { hasPermission } from '../../../helpers/permissionsHelper'

import { Resources } from '../../../model/RBAC/constants'

import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  useHttpError
} from '../../../hooks'

import moment from 'moment'
import { useSelector } from 'react-redux'
import { EnumPeriodoLiquidacion } from '../../../api'
import {
  ContractQuantity,
  ContractQuantityDetail
} from '../../../api/Argentia/endpoints/contracts/model/contracts'
import { FlexibilitySubtypeLimits } from '../../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { enumToSelectOptionArray, generateUniqueKeyWithNoPrefix } from '../../../helpers'
import { gridValuesGenerator } from '../../../helpers/gridValuesGenerator/gridValuesGenerator'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { selectActiveContract, selectClientData } from '../../../redux/selectors/client.selectors'
import { QuantityTable } from '../../flexibility/edit/components/quantityTable'
import {
  contractQuantityConsolidateMapper,
  gridRowToLimitDetailsList
} from '../../flexibility/flexibility.mapper'
import { Limit, LimitDetail, NewLimitDetail } from '../../flexibility/flexibility.Model'
import { fetchContractQuantityLimits } from '../../flexibility/flexibility.service'
import {
  limitToContractQuantityRequestMapper,
  limitToNewContractQuantityMapper
} from '../contracts.mapper'
import { createQuantitiesForContract, updateQuantitiesForContract } from '../contracts.service'
import { ContractQuantityCard } from './components/contractQuantityCard'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { getLinkClientText } from '../../clients/client.helper'

export interface ContractQuantitiesPageProps {
  contractID: number
}

const initialDetailTable = {
  year: new Date()
}

export const ContractQuantitiesPage: React.FC<any> = props => {
  const history = useHistory()
  const [t] = useTranslation(['contracts', 'globalerisLibrary', 'flexibilityDetails', 'enums'])

  const { getInformation, closeInformation } = useConfirmationDialog()
  const { info, error } = useNotify()

  const [contractQuantity, setContractQuantity] = useState<Limit | undefined>(undefined)
  const [contractQuantityDB, setContractQuantityDB] = useState<Limit>({} as ContractQuantity)
  const [isFetching, setIsFetching] = useState<boolean>(true)

  const clientData = useSelector(selectClientData)
  const contractData = useSelector(selectActiveContract)
  const contractID = props.location.state.contractID

  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()

  const [selectedDetail, setSelectedDetail] = useState<NewLimitDetail>(
    initialDetailTable as NewLimitDetail
  )

  interface formDependenciesProps {
    formState: any
  }

  const [formDependencies, setFormDependencies] = useState<formDependenciesProps>({
    formState: {}
  })

  const { showError, clearError, errorProps } = useHttpError()

  // Handlers Starts

  // Handlers Ends

  // Functions Starts

  const handleGoBack = () => {
    history.goBack()
  }

  const handleCreateContractQuantities = async (contractID: number, quantities: Limit) => {
    await createQuantitiesForContract(
      contractID,
      limitToNewContractQuantityMapper(quantities)
    ).then(response => {
      if (response.success) {
        info(t('contracts:forms.contractQuantities.notifications.save.success'))
        history.goBack()
      } else {
        error(t('contracts:forms.contractQuantities.notifications.save.error'))
      }
    })
  }

  const handleUpdateContractQuantities = async (contractID: number, quantities: Limit) => {
    await updateQuantitiesForContract(
      contractID,
      limitToContractQuantityRequestMapper(quantities)
    ).then(response => {
      if (response.success) {
        info(t('contracts:forms.contractQuantities.notifications.update.success'))
        history.goBack()
      } else {
        error(t('contracts:forms.contractQuantities.notifications.update.error'))
      }
    })
  }

  const onSave = async () => {
    if (!contractData?.id || !contractQuantity) {
      return
    }

    if (!contractQuantity?.id) {
      return handleCreateContractQuantities(contractData.id, contractQuantity)
    } else {
      return handleUpdateContractQuantities(contractData.id, contractQuantity)
    }
  }

  const fetchContractQuantities = async () => {
    if (!contractID) {
      return
    }

    await fetchContractQuantityLimits(contractID).then(response => {
      if (response.success) {
        const contractQuantityData = (response.data ? response.data : {}) as ContractQuantity
        setContractQuantity(contractQuantityData)
      } else {
        error(t('flexibilityDetails:notifications.errors.fetchContractQuantities'))
      }
    })
  }

  const handleAddRow = (detail: ContractQuantityDetail) => {
    const newDetail = {
      ...detail,
      tempId: generateUniqueKeyWithNoPrefix()
    }

    setContractQuantity(contractQuantityState => {
      return {
        ...contractQuantityState,
        details: contractQuantityState?.details
          ? [...contractQuantityState.details, newDetail]
          : [newDetail]
      }
    })
  }

  const handleEditRow = (detail: ContractQuantityDetail) => {
    const detailItem = contractQuantity?.details?.find(
      c =>
        (c.idDetail && c.idDetail === detail.idDetail) ||
        (!c.idDetail && c.tempId === detail.tempId)
    )

    const updatedDetail = {
      ...detailItem,
      ...detail,
      tempId: `${detailItem?.tempId ?? detailItem?.idDetail}`
    }

    setContractQuantity(prevState => {
      return {
        ...prevState,
        details: prevState?.details?.map(d => {
          if (
            (d.idDetail && d.idDetail === updatedDetail.idDetail) ||
            (!d.idDetail && d.tempId === updatedDetail.tempId)
          ) {
            return updatedDetail
          }
          return d
        })
      }
    })
  }

  const handleDeleteRow = (id: number | string) => {
    setContractQuantity(prevState => {
      return {
        ...prevState,
        details: prevState?.details?.filter(
          detail =>
            (detail.idDetail && detail.idDetail !== id) ||
            (!detail.idDetail && detail.tempId !== id)
        )
      }
    })
  }

  const openAssistantModal = (append?: boolean) => {
    openForm({
      definition: detailAssistantDefinition,
      title: t('flexibilityDetails:detailAssistant.title'),
      okBtnLabel: t('flexibilityDetails:detailAssistant.actions.okButton'),
      cancelBtnLabel: t('flexibilityDetails:detailAssistant.actions.cancelButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        formData ? handleGenerateValues(formData, append) : closeForm()
      }
    })
  }

  const handleGenerateValues = (formData, append?: boolean) => {
    const startDate = moment(formData.year).startOf('year').toDate()
    const endDate = moment(formData.year).endOf('year').toDate()
    const generatedValues = gridValuesGenerator({
      startDate,
      endDate,
      period: formData.idSettlementPeriod,
      fixedQuantity: 0
    })
    const limitDetails = gridRowToLimitDetailsList(generatedValues.value!)

    generatedValues?.value &&
      setContractQuantity(prevState => ({
        ...prevState,
        details: append
          ? prevState?.details
            ? [...prevState.details, ...(limitDetails as LimitDetail[])]
            : [...(limitDetails as LimitDetail[])]
          : (limitDetails as LimitDetail[])
      }))
    closeForm()
  }

  const detailAssistantDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'DETAIL_ASSISTANT',
        sections: [
          {
            inputs: [
              {
                name: 'subtitleHelpText',
                labelTitle: t('flexibilityDetails:detailAssistant.inputs.subtitleHelpText'),
                type: DataType.LABEL
              },
              {
                name: 'idSettlementPeriod',
                label: t('flexibilityDetails:detailAssistant.inputs.settlementPeriod'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoLiquidacion,
                  'enums:EnumPeriodoLiquidacion'
                ).filter(
                  option =>
                    option.value !== EnumPeriodoLiquidacion.Horario &&
                    option.value !== EnumPeriodoLiquidacion.Indefinido
                ),
                onChange(value, formState) {
                  setSelectedDetail(prevState => ({
                    ...prevState,
                    idSettlementPeriod: +value
                  }))
                }
              },
              {
                name: 'year',
                label: t('flexibilityDetails:detailAssistant.inputs.year'),
                type: DataType.DATE,
                views: ['year'],
                format: 'YYYY',
                defaultValue: moment(new Date()),
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const startDate = moment(value).startOf('year').toDate()
                    const endDate = moment(value).endOf('year').toDate()
                    setSelectedDetail(prevState => ({
                      ...prevState,
                      year: moment(value).toDate(),
                      startDate,
                      endDate
                    }))
                  }
                }
              }
            ]
          }
        ]
      }
    }, [])

  const handleOnChangeContractQuantity = (key: 'measureUnit', value: any) => {
    setContractQuantity(prevState => {
      let patch = prevState

      switch (key) {
        case 'measureUnit':
          patch = {
            ...prevState,
            measureUnit: value
          }
          break
      }

      return patch
    })
  }

  // Functions Ends

  // Hooks Starts

  useEffect(() => {
    fetchContractQuantities()
    // fetchFlexibilityLimitByID(props.location.state.flexibilityId)
  }, [])

  useEffect(() => {
    updateFormDefinition(detailAssistantDefinition, selectedDetail)
  }, [detailAssistantDefinition])

  // Hooks Ends

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} error={errorProps}>
      <PageCenterColumn>
        <PageHeader title={t('contracts:forms.contractQuantities.titles.main')}>
          <Button onClick={handleGoBack} size="small" color="primary">
            {t('contracts:forms.contractQuantities.mainActions.back')}
          </Button>
          <ButtonWithPermissions
            variant="contained"
            disabled={!contractQuantity?.measureUnit}
            onClick={onSave}
            size="small"
            color="primary"
            hasPermission={hasPermission(Resources.Flexibility, [UserPermission.EDIT])}
          >
            {t('contracts:forms.contractQuantities.mainActions.save')}
          </ButtonWithPermissions>
        </PageHeader>
        <>
          <Box mb={1}>
            <Breadcrumbs separator=">" aria-label="breadcrumb">
              {clientData && (
                <BreadcrumbLink toPathName="/client/file" state={{ clientID: clientData?.id }}>
                  {`Cliente ${getLinkClientText(clientData)} - ID ${clientData.documentID}`}
                </BreadcrumbLink>
              )}
              {contractData && (
                <BreadcrumbLink toPathName="/client/file" state={{ clientID: clientData?.id }}>
                  {t('contracts:forms.contractQuantities.breadcrumb.contract')}{' '}
                  {`${contractData.contractCode}`}
                </BreadcrumbLink>
              )}
              <Typography key="3" color="primary" variant="subtitle1">
                {t('contracts:forms.contractQuantities.breadcrumb.contractQuantities')}
              </Typography>
            </Breadcrumbs>
          </Box>
          <ContractQuantityCard
            key="quantitiesCard"
            contractQuantity={
              contractQuantity
                ? contractQuantityConsolidateMapper(contractQuantity)
                : contractQuantity
            }
            onAdd={() => openAssistantModal(true)}
            onChangeInput={(key, value) => handleOnChangeContractQuantity(key, value)}
          >
            <QuantityTable
              key="quantitiesTable"
              limitDetails={contractQuantity?.details}
              canEdit={true}
              onAddMassiveClickHandler={() => openAssistantModal(true)}
              onAddRowValue={row => handleAddRow(row)}
              onEditRowValues={row => handleEditRow(row)}
              onDeleteRow={row => handleDeleteRow(row)}
            />
          </ContractQuantityCard>
        </>

        <CustomFormDialog {...formProps}></CustomFormDialog>
      </PageCenterColumn>
      <PageRightColumn></PageRightColumn>
    </Page>
  )
}
